import axiosInstance from "service/axiosInstance";
import { PaginatedActivities } from "types/crm/activity";
import {
  CompanyAssociatedContact,
  Contact,
  ContactDetailResponse,
  ContactLifecycleStageApi,
  ContactOwner,
  CreateContactPayload,
  GetContactsParams,
  NonAssociatedContact,
  UpdateContactPayload,
} from "types/crm/contacts";
import { ContactOwnersResponse } from "types/crm/contacts";
import { NonAssociatedDealParams } from "types/crm/deal";
import { NonAssociatedCompanyContactParams } from "types/crm/company";
import { CrmPage } from "types/crm/pagination";
import { toCrmPage } from "./pagination";
import { CreateCrmContactNotePayload, Note, NoteResponse, NotesListResponse, PaginatedNotes, UpdateCrmContactNotePayload } from "types/crm/notes";
import { CreateCrmContactTaskPayload, CrmTask, PaginatedTasks, TaskResponse, TasksListResponse, TaskStatus, TaskQuickFilter } from "types/crm/task";
import { CrmComment, CrmCommentRepliesPage } from "types/crm/comments";

/**
 * Payload for updating a contact task. All fields besides the identifiers are
 * optional so callers can send a partial patch (e.g. just `status`).
 * NOTE: ideally this lives in types/crm/task.ts alongside CreateCrmContactTaskPayload;
 * declared here for now so nothing else has to change shape.
 */
export interface UpdateCrmContactTaskPayload {
  contactUuid: string;
  taskUuid: string;
  title?: string;
  description?: string;
  dueAt?: string;
  taskOwnerUuid?: string | null;
  taskType?: string;
  priority?: string;
  timezone?: string;
  status?: TaskStatus;
}

export const contactQueryKeys = {
  all: ["contacts"] as const,
  lists: () => [...contactQueryKeys.all, "list"] as const,
  list: (params: GetContactsParams) =>
    [...contactQueryKeys.lists(), params] as const,
  resolve: (contactKey: string) =>
    [...contactQueryKeys.all, "resolve", contactKey] as const,
  detail: (contactUuid: string) =>
    [...contactQueryKeys.all, "detail", contactUuid] as const,
  owners: (orgUuid?: string, search?: string) =>
    [...contactQueryKeys.all, "owners", orgUuid ?? "", search ?? ""] as const,
  notes: (contactUuid: string) =>
    [...contactQueryKeys.all, "notes", contactUuid] as const,
  tasks: (contactUuid: string, status?: TaskStatus, quickFilter?: TaskQuickFilter) =>
    [...contactQueryKeys.all, "tasks", contactUuid, status ?? "ALL", quickFilter ?? "NONE"] as const,
  activities: (contactUuid: string) =>
    [...contactQueryKeys.all, "activities", contactUuid] as const,
  byDeal: (dealUuid: string) =>
    [...contactQueryKeys.all, "deal", dealUuid] as const,
  byCompany: (companyUuid: string) =>
    [...contactQueryKeys.all, "company", companyUuid] as const,
  nonAssociatedByCompany: (
    companyUuid: string,
    params: Omit<NonAssociatedCompanyContactParams, "companyUuid"> = {},
  ) =>
    [
      ...contactQueryKeys.byCompany(companyUuid),
      "non-associated",
      params,
    ] as const,
  nonAssociatedByDeal: (
    dealUuid: string,
    params: Omit<NonAssociatedDealParams, "dealUuid"> = {},
  ) =>
    [
      ...contactQueryKeys.byDeal(dealUuid),
      "non-associated",
      params,
    ] as const,
  noteComments: (contactUuid: string, contactNoteUuid: string) =>
    [...contactQueryKeys.all, "note-comments", contactUuid, contactNoteUuid] as const,
  noteCommentReplies: (contactUuid: string, contactNoteUuid: string, commentUuid: string, page: number) =>
    [...contactQueryKeys.noteComments(contactUuid, contactNoteUuid), "replies", commentUuid, page] as const,
  taskComments: (contactUuid: string, contactTaskUuid: string) =>
    [...contactQueryKeys.all, "task-comments", contactUuid, contactTaskUuid] as const,
  taskCommentReplies: (contactUuid: string, contactTaskUuid: string, commentUuid: string, page: number) =>
    [...contactQueryKeys.taskComments(contactUuid, contactTaskUuid), "replies", commentUuid, page] as const,
};

const dealContactPaths = {
  nonAssociated: (dealUuid: string) =>
    `/crm/deals/${dealUuid}/contacts/non-associated`,
};

/**
 * Logs `GET /crm/contacts/{contactUuid}` and nothing else.
 *
 * Development only: CRA inlines `process.env.NODE_ENV` at build time, so these
 * calls are dropped from a production bundle rather than merely kept quiet.
 * Scoped to this one endpoint deliberately — it is here to see what the contact
 * detail actually returns, not to narrate every request the app makes.
 */
const logContactDetailCall = (label: string, payload: unknown) => {
  if (process.env.NODE_ENV !== "development") return;
  // eslint-disable-next-line no-console
  console.log(`[GET /crm/contacts/{uuid}] ${label}`, payload);
};

export const contactApi = {
  getContacts: async ({
    search,
    lifecycleStage,
    page = 0,
    size = 20,
  }: GetContactsParams): Promise<CrmPage<Contact, ContactLifecycleStageApi>> => {
    const { data } = await axiosInstance.get("/crm/contacts", {
      params: {
        search: search?.trim() || undefined,
        lifecycleStage: lifecycleStage || undefined,
        page,
        size,
      },
    });
    return toCrmPage<Contact, ContactLifecycleStageApi>(data.data, page, size);
  },

  // Step 1: contactKey -> uuid

  resolveContactUuidByKey: async (contactKey: string): Promise<string> => {
    // console.log("Resolving contactKey to uuid:", contactKey);
    const { data } = await axiosInstance.get(
      `/crm/contacts/key/${contactKey}/uuid`,
    );
    return data.data.uuid;
  },

  // Step 2: uuid -> full contact details
  getContact: async (contactUuid: string): Promise<Contact> => {
    logContactDetailCall("request", `GET /crm/contacts/${contactUuid}`);

    const { data } = await axiosInstance.get<ContactDetailResponse>(
      `/crm/contacts/${contactUuid}`,
    );

    // The whole envelope, then the contact on its own — the first says whether
    // the call succeeded, the second is what the screens actually bind to.
    logContactDetailCall("response (envelope)", data);
    logContactDetailCall("response (contact)", data.data);

    return data.data;
  },

  getContactOwners: async (orgUuid?: string, search?: string): Promise<ContactOwner[]> => {
    const params: Record<string, string> = {};
    if (search !== undefined) {
      params.search = search;
    }
    const { data } =
      await axiosInstance.get<ContactOwnersResponse>(`/crm/contacts/owners`, {
        params: Object.keys(params).length > 0 ? params : undefined,
      });
    return data.data;
  },

  createContact: async (payload: CreateContactPayload): Promise<Contact> => {
    const { data } = await axiosInstance.post(`crm/contacts`, payload);
    return data;
  },

  updateContact: async (payload: UpdateContactPayload): Promise<Contact> => {
    const { contactUuid, ...rest } = payload;
    const { data } = await axiosInstance.patch(
      `crm/contacts/${contactUuid}`,
      rest,
    );
    return data;
  },

  // ---------------------------------------------------------------------------
  // Notes APIs
  // ---------------------------------------------------------------------------


  getContactNotes: async (
    contactUuid: string,
    page = 0,
    size = 20,
  ): Promise<PaginatedNotes> => {
    const { data } = await axiosInstance.get<NotesListResponse>(
      `/crm/contacts/${contactUuid}/notes`,
      { params: { page, size } },
    );
    return data.data;
  },

  addContactNote: async (payload: CreateCrmContactNotePayload): Promise<Note> => {
    const { contactUuid, ...rest } = payload;
    const { data } = await axiosInstance.post<NoteResponse>(
      `/crm/contacts/${contactUuid}/notes`,
      rest,
    );
    return data.data;
  },

  updateContactNote: async (payload: UpdateCrmContactNotePayload): Promise<CrmTask> => {
    const { contactUuid, noteUuid, ...rest } = payload;
    const { data } = await axiosInstance.put<TaskResponse>(
      `/crm/contacts/${contactUuid}/notes/${noteUuid}`,
      rest,
    );
    return data.data;
  },


  deleteContactNote: async (contactUuid: string, noteUuid: string): Promise<void> => {
    await axiosInstance.delete(`/crm/contacts/${contactUuid}/notes/${noteUuid}`);
  },

  // ---------------------------------------------------------------------------
  // Task APIs
  // ---------------------------------------------------------------------------

  getContactTasks: async (
    contactUuid: string,
    page = 0,
    size = 20,
    status?: TaskStatus,
    quickFilter?: TaskQuickFilter,
  ): Promise<PaginatedTasks> => {
    const { data } = await axiosInstance.get<TasksListResponse>(
      `/crm/contacts/${contactUuid}/tasks`,
      { params: { page, size, status: status || undefined, quickFilter } },
    );
    return data.data;
  },

  createContactTask: async (payload: CreateCrmContactTaskPayload): Promise<CrmTask> => {
    const { contactUuid, ...rest } = payload;
    const { data } = await axiosInstance.post<TaskResponse>(
      `/crm/contacts/${contactUuid}/tasks`,
      rest,
    );
    return data.data;
  },

  updateContactTask: async (payload: UpdateCrmContactTaskPayload): Promise<CrmTask> => {
    const { contactUuid, taskUuid, ...rest } = payload;
    const { data } = await axiosInstance.patch<TaskResponse>(
      `/crm/contacts/${contactUuid}/tasks/${taskUuid}`,
      rest,
    );
    return data.data;
  },

  deleteContactTask: async (contactUuid: string, taskUuid: string): Promise<void> => {
    await axiosInstance.delete(`/crm/contacts/${contactUuid}/tasks/${taskUuid}`);
  },

  getNonAssociatedDealContacts: async ({
    dealUuid,
    search,
    page = 0,
    limit = 20,
  }: NonAssociatedDealParams): Promise<CrmPage<NonAssociatedContact>> => {
    const { data } = await axiosInstance.get(
      dealContactPaths.nonAssociated(dealUuid),
      { params: { search: search?.trim() || undefined, page, limit } },
    );
    return toCrmPage<NonAssociatedContact>(data.data, page, limit);
  },

  /** Contacts associated with a company — the path takes a reference, we always send the uuid. */
  getContactsByCompany: async (
    companyUuid: string,
  ): Promise<CompanyAssociatedContact[]> => {
    const { data } = await axiosInstance.get(
      `/crm/companies/${companyUuid}/contacts`,
    );
    return data.data?.content ?? data.data ?? [];
  },

  getNonAssociatedCompanyContacts: async ({
    companyUuid,
    search,
    page = 0,
    limit = 20,
  }: NonAssociatedCompanyContactParams): Promise<
    CrmPage<NonAssociatedContact>
  > => {
    const { data } = await axiosInstance.get(
      `/crm/companies/${companyUuid}/contacts/non-associated`,
      { params: { search: search?.trim() || undefined, page, limit } },
    );
    return toCrmPage<NonAssociatedContact>(data.data, page, limit);
  },

  associateContactWithCompany: async ({
    companyUuid,
    contactUuid,
  }: {
    companyUuid: string;
    contactUuid: string;
  }): Promise<Contact> => {
    const { data } = await axiosInstance.post(
      `/crm/companies/${companyUuid}/contacts/${contactUuid}`,
    );
    return data.data;
  },

  getContactActivities: async (
    contactUuid: string,
    page = 0,
    size = 20,
    tab = "ALL_ACTIVITIES",
  ): Promise<PaginatedActivities> => {
    const { data } = await axiosInstance.get<{ data: PaginatedActivities }>(
      `/crm/contacts/${contactUuid}/activities`,
      { params: { tab, page, size } },
    );
    return data.data;
  },

  deleteContact: async (contactUuid: string): Promise<void> => {
    await axiosInstance.delete(`crm/contacts/${contactUuid}`);
  },

  // ---------------------------------------------------------------------------
  // Comment APIs
  // ---------------------------------------------------------------------------
  addContactNoteComment: async (
    contactUuid: string,
    contactNoteUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.post(
      `/crm/contacts/${contactUuid}/notes/${contactNoteUuid}/comments`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  updateContactNoteComment: async (
    contactUuid: string,
    contactNoteUuid: string,
    commentUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.put(
      `/crm/contacts/${contactUuid}/notes/${contactNoteUuid}/comments/${commentUuid}`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  deleteContactNoteComment: async (
    contactUuid: string,
    contactNoteUuid: string,
    commentUuid: string,
  ): Promise<void> => {
    await axiosInstance.delete(
      `/crm/contacts/${contactUuid}/notes/${contactNoteUuid}/comments/${commentUuid}`,
    );
  },

  getContactNoteCommentReplies: async (
    contactUuid: string,
    contactNoteUuid: string,
    commentUuid: string,
    page = 0,
    size = 20,
  ): Promise<CrmCommentRepliesPage> => {
    const { data } = await axiosInstance.get(
      `/crm/contacts/${contactUuid}/notes/${contactNoteUuid}/comments/${commentUuid}/replies`,
      { params: { page, size } },
    );
    return data.data;
  },

  addContactTaskComment: async (
    contactUuid: string,
    contactTaskUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.post(
      `/crm/contacts/${contactUuid}/tasks/${contactTaskUuid}/comments`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  updateContactTaskComment: async (
    contactUuid: string,
    contactTaskUuid: string,
    commentUuid: string,
    commentText: string,
    parentCommentUuid?: string | null,
  ): Promise<CrmComment> => {
    const { data } = await axiosInstance.put(
      `/crm/contacts/${contactUuid}/tasks/${contactTaskUuid}/comments/${commentUuid}`,
      { commentText, parentCommentUuid: parentCommentUuid ?? null },
    );
    return data.data;
  },

  deleteContactTaskComment: async (
    contactUuid: string,
    contactTaskUuid: string,
    commentUuid: string,
  ): Promise<void> => {
    await axiosInstance.delete(
      `/crm/contacts/${contactUuid}/tasks/${contactTaskUuid}/comments/${commentUuid}`,
    );
  },

  getContactTaskCommentReplies: async (
    contactUuid: string,
    contactTaskUuid: string,
    commentUuid: string,
    page = 0,
    size = 20,
  ): Promise<CrmCommentRepliesPage> => {
    const { data } = await axiosInstance.get(
      `/crm/contacts/${contactUuid}/tasks/${contactTaskUuid}/comments/${commentUuid}/replies`,
      { params: { page, size } },
    );
    return data.data;
  },
};